import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

import { useRecoilValue } from 'recoil';

import { pm } from 'lib/helpers/utility';

import Button from 'components/shared/Buttons/Button';
import DocumentModal from 'components/shared/DocumentEditor/DocumentModal';
import getEditableDocumentType from 'components/shared/DocumentEditor/lib/getEditableDocumentType';
import state, { clinicalNote } from 'components/state';

import Modal from '../Modal/Modal';

import EditDocument from './components/EditDocument';
import PreviewDocument from './components/PreviewDocument';
import useDocumentHandler from './lib/useDocumentHandler';

export default function View({
  viewDocument,
  setViewDocument,
  handleCloseModal,
  action,
  patientId,
  handle,
  setSendFaxModal,
  type,
  patient,
  options,
  appointmentId,
  categories
}) {
  const { locked: isLocked } = useRecoilValue(clinicalNote);

  const submitFormButton = useRef();

  const exportRef = useRef();

  const print = useReactToPrint({
    content: () => exportRef.current,
    documentTitle: `hipaa-${patient ? patient?.f_name + ' ' + patient?.l_name : 'Patient'}.pdf`
  });

  const permissions = useRecoilValue(state.permissions);

  const canUpdate =
    ((viewDocument?.patient_id === viewDocument?.uploader_id && options?.isPatient) ||
      (options?.enableDocumentEdit && pm(permissions, 'patient_documents_center.update'))) &&
    viewDocument?.kind !== 'fax';

  const [edit, setEdit] = useState({
    name: viewDocument?.title,
    category: viewDocument?.category
  });

  const [loading, setLoading] = useState({
    personal_id_front: false,
    personal_id_back: false,
    insurance_front: false,
    insurance_back: false,
    filestack: false
  });
  const [url, setUrl] = useState();
  const [hipaaData, setHipaaData] = useState();

  const { handleEdit, getUrl, editLoading } = useDocumentHandler({
    patientId,
    appointmentId,
    handleCloseModal
  });

  const handleFormSubmit = async () => {
    if (submitFormButton.current) {
      submitFormButton.current.click();
    }
  };

  const onEdit = () => handleEdit({ handle, edit, viewDocument });

  useEffect(() => {
    if (handle && type !== 'form')
      getUrl({
        type,
        handle,
        setUrl,
        setHipaaData,
        setLoading
      });
  }, [handle]);

  if (action !== 'Edit' && getEditableDocumentType(viewDocument.type)) {
    return (
      <DocumentModal
        {...{
          src: type !== 'fax' ? url : null,
          id: viewDocument?.id,
          kind: type === 'fax' ? 'fax' : 'patient_documents',
          type: getEditableDocumentType(viewDocument.type),
          handleOpen: !!viewDocument,
          handleClose: handleCloseModal,
          isEditable:
            !isLocked &&
            options?.enableDocumentEdit &&
            pm(permissions, 'patient_documents_center.update'),
          title: viewDocument?.title,
          isFooter: !isLocked && canUpdate,
          footer:
            isLocked || !canUpdate ? null : (
              <div className="flex-end flex w-full justify-between gap-2">
                <Button
                  className="mr-auto"
                  text="Cancel"
                  outlined
                  color="neutral"
                  onClick={handleCloseModal}
                />

                <div className="flex gap-2">
                  {options?.enableSendAsFax && (
                    <Button
                      text="Send as Fax"
                      color="neutral"
                      outlined
                      icon="send-mono"
                      onClick={() => setSendFaxModal({ show: true, files: [viewDocument] })}
                    />
                  )}
                  <Button text="Update" loading={editLoading} primary onClick={onEdit} />
                </div>
              </div>
            ),

          subheaderComponent:
            isLocked || !canUpdate ? null : (
              <EditDocument
                {...{
                  edit,
                  setEdit,
                  handle,
                  type,
                  action: 'Edit',
                  loading,
                  patientId: viewDocument?.patient_id,
                  options,
                  submitFormButton,
                  handleCloseModal,
                  categories,
                  inline: true,
                  onEdit,
                  appointmentId
                }}
              />
            )
        }}
      />
    );
  }

  return (
    <Modal
      customHeader={
        <div className="mr-4 flex w-full justify-between">
          <p className="text-ellipsis text-lg">{viewDocument?.title}</p>
          {viewDocument?.type === 'hipaa' && (
            <Button
              size="small"
              text="Print"
              iconColor="white"
              icon="printer-mono"
              onClick={() => print()}
            />
          )}
        </div>
      }
      handleOpen={!!viewDocument}
      handleClose={handleCloseModal}
      isLarge
      className={
        ['hipaa', 'insurance'].includes(viewDocument.type) ? 'max-w-[1280px]' : 'max-w-[640px]'
      }
      slideFromRight
      footer={
        viewDocument?.action === 'Edit' && (
          <div className="flex-end flex gap-2">
            <Button
              className="mr-auto"
              text="Cancel"
              outlined
              color="neutral"
              onClick={handleCloseModal}
            />

            <Button
              text="Update"
              primary
              onClick={viewDocument?.type === 'form' ? handleFormSubmit : onEdit}
            />
          </div>
        )
      }>
      {action === 'Edit' ? (
        <EditDocument
          {...{
            edit,
            setEdit,
            handle,
            type,
            action,
            options,
            loading,
            patientId: viewDocument?.patient_id,
            inline: true,
            submitFormButton,
            handleCloseModal,
            categories,
            appointmentId
          }}
        />
      ) : (
        <PreviewDocument
          {...{
            url,
            hipaaData,
            setLoading,
            exportRef,
            handle,
            type,
            patientId,
            setViewDocument,
            loading
          }}
        />
      )}
    </Modal>
  );
}
